/**
 * Generic Node View
 *
 * Fallback view for nodes without a dedicated PlayerView.
 * In production, shows a clean waiting spinner.
 * In dev mode, shows node state, config, and a manual action form.
 */

import { useState } from "react";
import { Button } from "../../components/ui/Button";
import type { NodePlayerViewProps } from "./types";

export function GenericNodeView(props: NodePlayerViewProps) {
  if (import.meta.env.DEV) {
    return <GenericNodeDevView {...props} />;
  }

  const label = props.nodeState.nodeType
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());

  return (
    <div className="@container rounded-xl bg-surface-900 border border-surface-700/50">
      <div className="px-4 @md:px-8 py-12 text-center">
        <h3 className="font-display text-xs @sm:text-sm font-semibold uppercase tracking-widest text-brand-400/80 mb-2">
          {label}
        </h3>
        <p className="text-sm text-text-secondary mb-4">
          This step isn't supported yet. Try refreshing the page.
        </p>
        <Button size="sm" variant="secondary" onClick={() => window.location.reload()}>
          Refresh
        </Button>
      </div>
    </div>
  );
}

function GenericNodeDevView({ nodeState, onAction }: NodePlayerViewProps) {
  const [actionType, setActionType] = useState("");
  const [payloadJson, setPayloadJson] = useState("{}");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    if (!actionType.trim()) return;

    setIsSubmitting(true);
    setError(null);

    let payload: unknown;
    try {
      payload = JSON.parse(payloadJson);
    } catch {
      setError("Invalid JSON payload");
      setIsSubmitting(false);
      return;
    }

    const result = await onAction(actionType, payload);
    if (!result.success && result.error) {
      setError(result.error);
    }

    setIsSubmitting(false);
  };

  return (
    <div className="@container relative overflow-hidden rounded-xl bg-surface-900 border border-surface-700/50">
      {/* Atmospheric background effects */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] rounded-full bg-brand-500/[0.04] blur-[100px]" />
        <div className="absolute inset-0 noise-overlay" />
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-brand-500/30 to-transparent" />
      </div>

      <div className="relative z-10 px-4 @md:px-8 @3xl:px-10 py-8 @md:py-10">
        <div className="text-center mb-6">
          <h3 className="font-display text-xs @sm:text-sm font-semibold uppercase tracking-[0.3em] text-brand-400/80 mb-2">
            {nodeState.nodeType.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}
          </h3>
          <p className="text-sm text-text-secondary">
            No dedicated view — use the action form below
          </p>
        </div>

        {/* Current state */}
        <div className="mb-6">
          <h4 className="text-sm font-medium text-text-secondary mb-2">Node State</h4>
          <pre className="bg-surface-800/60 rounded-lg p-3 text-xs text-text-primary overflow-auto max-h-48 font-mono border border-surface-700/40">
            {JSON.stringify(nodeState.state, null, 2)}
          </pre>
        </div>

        {/* Config */}
        <div className="mb-6">
          <h4 className="text-sm font-medium text-text-secondary mb-2">Config</h4>
          <pre className="bg-surface-800/60 rounded-lg p-3 text-xs text-text-primary overflow-auto max-h-32 font-mono border border-surface-700/40">
            {JSON.stringify(nodeState.config, null, 2)}
          </pre>
        </div>

        {/* Action form */}
        <div className="border-t border-surface-700/40 pt-4">
          <h4 className="text-sm font-medium text-text-secondary mb-3">Submit Action</h4>

          <div className="space-y-3">
            <div>
              <label className="block text-xs text-text-secondary mb-1">Action Type</label>
              <input
                type="text"
                value={actionType}
                onChange={(e) => setActionType(e.target.value)}
                placeholder="e.g. vote, confirm, select"
                className="w-full px-3 py-2 bg-surface-800 border border-surface-600 rounded-lg text-sm text-text-primary placeholder:text-surface-500 focus:outline-none focus:ring-2 focus:ring-brand-500"
              />
            </div>

            <div>
              <label className="block text-xs text-text-secondary mb-1">Payload (JSON)</label>
              <textarea
                value={payloadJson}
                onChange={(e) => setPayloadJson(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 bg-surface-800 border border-surface-600 rounded-lg text-sm text-text-primary font-mono placeholder:text-surface-500 focus:outline-none focus:ring-2 focus:ring-brand-500 resize-none"
              />
            </div>

            {error && <p className="text-error-500 text-sm">{error}</p>}

            <Button
              onClick={handleSubmit}
              disabled={isSubmitting || !actionType.trim()}
              size="sm"
              className="w-full"
            >
              {isSubmitting ? "Submitting..." : "Submit Action"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
